import React, { useEffect, useMemo, useState } from "react";
import { CheckSquare, Square } from "@phosphor-icons/react";
import api from "@/lib/api";

// Hardcoded verticals every affiliate can be approved for. Keep in sync
// with `_LANDING_SLUG_TO_ACCIDENT_TYPE` in backend/routes/public_routes.py.
export const HARDCODED_VERTICAL_SLUGS = [
  { slug: "auto-accident",       label: "Auto Accident" },
  { slug: "truck-accident",      label: "Truck Accident" },
  { slug: "rideshare-accident",  label: "Rideshare (Uber / Lyft)" },
  { slug: "motorcycle-accident", label: "Motorcycle" },
  { slug: "personal-injury",     label: "Personal Injury (all-in-one)" },
  { slug: "free-case-review",    label: "Free Case Review (all-in-one)" },
];

// Shared global funnels — neutral traffic is matched to a buyer from the
// visitor's answers. Buyer-coded links (`?b=`) need the buyer approved.
export const GLOBAL_FUNNEL_SLUGS = [
  { slug: "standard", label: "Standard Form" },
  { slug: "quiz",     label: "Quiz-Style Form" },
  { slug: "quiz-v2",  label: "Quiz V2 (App-Style)" },
  { slug: "focused",  label: "Focused Funnel" },
  { slug: "auto",     label: "Car Accident" },
  { slug: "truck",    label: "Truck / 18-Wheeler" },
  { slug: "rideshare", label: "Rideshare (Uber / Lyft)" },
  { slug: "motorcycle", label: "Motorcycle" },
  { slug: "injury",   label: "Personal Injury (All-in-One)" },
];

/**
 * Iter-66 — Admin picker for `organizations.allowed_landing_page_slugs`
 * on affiliate orgs.
 *
 * Renders two blocks of checkboxes:
 *   1. Hardcoded verticals (`auto-accident`, `truck-accident`, …).
 *   2. Every currently PUBLISHED custom landing page slug (fetched
 *      on-mount, 100-row cap — well beyond real production custom-LP
 *      counts).
 *
 * A slug the affiliate is approved for that is neither in the
 * hardcoded list nor in the published set (e.g. custom page was paused
 * or removed after approval) is rendered under a third "Approved but
 * no longer live" section so the admin can uncheck it explicitly.
 *
 * `value` — string[] of approved slugs.
 * `onChange(next: string[])` — parent stores + submits on Save.
 */
export default function LandingPageAllowlistPicker({
  value = [],
  onChange,
  testIdPrefix = "aff-lp",
}) {
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const shownPages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customPages;
    return customPages.filter((c) => `${c.name} ${c.slug} ${c.firm_name || ""}`.toLowerCase().includes(q));
  }, [customPages, query]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Iter-62.2.1 paginated shape ({items,total}) — walk every page.
        const pages = [];
        for (let p = 1; p <= 50; p += 1) {
          const { data } = await api.get(
            `/admin/custom-landing-pages?status=published&page_size=100&sort=name_asc&page=${p}`,
          );
          const items = data.items || data.pages || [];
          pages.push(...items);
          if (items.length < 100 || pages.length >= (data.total || 0)) break;
        }
        if (cancelled) return;
        setCustomPages(
          pages
            .filter((p) => p && p.slug)
            .map((p) => ({
              slug: p.slug,
              name: p.name || p.firm_name || p.slug,
              firm_name: p.firm_name || null,
            })),
        );
      } catch {
        if (!cancelled) setCustomPages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = useMemo(() => new Set(value || []), [value]);

  const toggle = (slug) => {
    const set = new Set(value || []);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    onChange?.(Array.from(set));
  };

  const clearAll = () => onChange?.([]);
  const selectAllVerticals = () => {
    const next = new Set(value || []);
    HARDCODED_VERTICAL_SLUGS.forEach((v) => next.add(v.slug));
    onChange?.(Array.from(next));
  };

  const knownSlugs = new Set([
    ...HARDCODED_VERTICAL_SLUGS.map((v) => v.slug),
    ...GLOBAL_FUNNEL_SLUGS.map((v) => v.slug),
    ...customPages.map((c) => c.slug),
  ]);
  const orphanApproved = (value || []).filter((s) => !knownSlugs.has(s));

  const Row = ({ slug, label, sub }) => {
    const on = selected.has(slug);
    return (
      <button
        type="button"
        onClick={() => toggle(slug)}
        data-testid={`${testIdPrefix}-opt-${slug}`}
        className={`w-full text-left px-3 py-2 border border-black inline-flex items-start gap-2 hover:bg-zinc-100 ${on ? "bg-emerald-50" : "bg-white"}`}
      >
        {on
          ? <CheckSquare size={14} weight="fill" className="mt-0.5 text-emerald-800 flex-shrink-0" />
          : <Square size={14} weight="bold" className="mt-0.5 text-zinc-400 flex-shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold truncate">{label}</span>
          <span className="block text-[10px] font-mono text-zinc-500 truncate">/lp/{slug}{sub ? ` · ${sub}` : ""}</span>
        </span>
      </button>
    );
  };

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="label-mono">
          {value.length === 0
            ? "No pages approved — every affiliate-credited /lp/ hit gets 403"
            : `${value.length} page${value.length === 1 ? "" : "s"} approved`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={selectAllVerticals}
            data-testid={`${testIdPrefix}-select-verticals`}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            Approve all verticals
          </button>
          {value.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              data-testid={`${testIdPrefix}-clear`}
              className="border border-red-700 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white"
            >
              Unapprove all
            </button>
          )}
        </div>
      </div>

      <div className="mb-4" data-testid={`${testIdPrefix}-global-funnels`}>
        <div className="label-mono mb-1.5">Shared funnels</div>
        <div className="text-[10.5px] text-zinc-600 leading-relaxed mb-2">
          One page set for every buyer. Without a buyer code, the firm is
          picked from the visitor&apos;s answers using normal routing.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {GLOBAL_FUNNEL_SLUGS.map((v) => (
            <Row key={v.slug} slug={v.slug} label={v.label} sub="shared" />
          ))}
        </div>
      </div>

      <div className="mb-4" data-testid={`${testIdPrefix}-verticals`}>
        <div className="label-mono mb-1.5">Standard verticals</div>
        <div className="text-[10.5px] text-zinc-600 leading-relaxed mb-2">
          Marketplace routing — every lead fans out to every buyer whose
          intake criteria match, no matter which of these pages it came from.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {HARDCODED_VERTICAL_SLUGS.map((v) => (
            <Row key={v.slug} slug={v.slug} label={v.label} />
          ))}
        </div>
      </div>

      <div className="mb-2" data-testid={`${testIdPrefix}-custom`}>
        <div className="label-mono mb-1.5">
          Custom buyer landing pages
          {!loading && customPages.length > 0 && (
            <span className="ml-2 normal-case text-zinc-500 text-[10px]">
              ({customPages.length} published)
            </span>
          )}
        </div>
        <div className="text-[10.5px] text-zinc-600 leading-relaxed mb-2">
          Buyer-branded — leads submitted here route <b>directly</b> to that
          specific buyer's destination (marketplace matching is bypassed).
          Approve one to let this affiliate push traffic to that buyer's page.
        </div>
        {loading && (
          <div className="text-[11px] text-zinc-500">Loading custom pages…</div>
        )}
        {!loading && customPages.length === 0 && (
          <div className="text-[11px] text-zinc-500" data-testid={`${testIdPrefix}-custom-empty`}>
            No custom landing pages are currently published.
          </div>
        )}
        {!loading && customPages.length > 0 && (
          <input
            className="border border-black px-2 py-1.5 text-xs w-full md:w-1/2 mb-2"
            placeholder="Search buyer pages by name, firm or slug…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid={`${testIdPrefix}-custom-search`}
          />
        )}
        {!loading && customPages.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {shownPages.map((c) => (
              <Row key={c.slug} slug={c.slug} label={c.name} sub={c.firm_name} />
            ))}
          </div>
        )}
      </div>

      {orphanApproved.length > 0 && (
        <div className="mt-3 border border-amber-600 bg-amber-50 p-3" data-testid={`${testIdPrefix}-orphans`}>
          <div className="label-mono text-amber-900 mb-1.5">
            Approved but no longer live ({orphanApproved.length})
          </div>
          <div className="text-[10px] text-zinc-600 mb-2">
            These slugs are still on the allowlist but no matching custom
            landing page is currently published. Uncheck to revoke.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {orphanApproved.map((slug) => (
              <Row key={slug} slug={slug} label={slug} sub="unpublished / unknown" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
